import type { TranslationKey } from "@/lib/i18n";
import type { InboxNotification } from "@/lib/autar/notifications";
import { workRenderers } from "./renderersWork";

type Translate = (
  key: TranslationKey,
  params?: Record<string, string | number>,
) => string;

export interface RenderedNotification {
  title: string;
  body: string;
  /** App path the item opens; the notifications page when nothing better is known. */
  href: string;
}

type Renderer = (
  n: InboxNotification,
  t: Translate,
  locale: string,
) => Omit<RenderedNotification, "href"> & { href?: string | null };

/**
 * One renderer per notification `type`. Text comes from t() keys and the
 * stored structured fields, never from server-rendered copy. Types register
 * here as they ship (the CVE types arrive with their own change).
 */
export const notificationRenderers: Record<string, Renderer> = {
  ...workRenderers,
  "cve.advisory_published": (n, t) => cveRow(n, t, "published"),
  "cve.fix_available": (n, t) => cveRow(n, t, "fix"),
  "cve.severity_changed": (n, t) => cveRow(n, t, "severity"),
  "cve.advisory_withdrawn": (n, t) => cveRow(n, t, "withdrawn"),
  "cve.repository_digest": (n, t, locale) => {
    const entries = (
      Array.isArray(n.data.entries) ? n.data.entries : []
    ) as Record<string, unknown>[];
    const count = entries.length;
    const parts = severities
      .map(
        (s) =>
          [s, entries.filter((e) => inboxSeverity(e.severity) === s)] as const,
      )
      .filter(([, list]) => list.length)
      .map(([s, list]) =>
        t("notifications.cve.digest.count" as TranslationKey, {
          count: list.length,
          severity: severityLabel(s, t).toLocaleLowerCase(locale),
        }),
      );
    // Entries arrive worst first, so the first with a fix is the one to act on.
    const fix = entries.find((e) => text(e.fixedIn));
    if (fix)
      parts.push(
        t("notifications.cve.digest.upgrade" as TranslationKey, {
          package: text(fix.packageName),
          fixedIn: text(fix.fixedIn),
        }),
      );
    return {
      title: t(
        `notifications.cve.digest.${count === 1 ? "titleOne" : "title"}` as TranslationKey,
        {
          severity: severityLabel(n.data.severity, t),
          count,
          repository: text(n.data.repositoryFullName),
        },
      ),
      body: parts.join(" · "),
    };
  },
  "cve.baseline_summary": (n, t, locale) => {
    const repositories = Array.isArray(n.data.repositories)
      ? (n.data.repositories as Record<string, unknown>[])
      : [];
    const count = repositories.reduce(
      (sum, r) =>
        sum + num(r.critical) + num(r.high) + num(r.medium) + num(r.low),
      0,
    );
    return {
      title: t(
        `notifications.cve.baseline.${count === 1 ? "titleOne" : "title"}` as TranslationKey,
        {
          severity: severityLabel(n.data.severity, t),
          count: new Intl.NumberFormat(locale).format(count),
          estate: text(n.data.estateName),
        },
      ),
      body: t("notifications.cve.baseline.body" as TranslationKey, {
        repositories: new Intl.NumberFormat(locale).format(repositories.length),
      }),
    };
  },
};

// Only same-app paths: the payload is data, and a link must not leave the app.
const appPath = (value: unknown) =>
  typeof value === "string" && /^\/(?![/\\])/.test(value) ? value : null;

const text = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const severities = ["critical", "high", "medium", "low"] as const;

// Advisory feeds say "moderate"; the inbox knows "medium".
const inboxSeverity = (value: unknown) =>
  value === "moderate" ? "medium" : value;

// The severity is written into the title, so a row never relies on colour alone.
const severityLabel = (value: unknown, t: Translate) =>
  t(
    `notifications.severity.${severities.find((s) => s === value) ?? "info"}` as TranslationKey,
  );

type CveKind = "published" | "fix" | "severity" | "withdrawn";

function cveRow(
  n: InboxNotification,
  t: Translate,
  kind: CveKind,
): Omit<RenderedNotification, "href"> {
  const d = n.data;
  const params = {
    severity: severityLabel(d.severity, t),
    previous: severityLabel(d.previousSeverity, t),
    advisory: text(d.advisoryId),
    package: text(d.packageName),
    version: text(d.version),
    fixedIn: text(d.fixedIn),
    repository: text(d.repositoryFullName),
  };
  const body =
    kind === "published" && !params.fixedIn
      ? "published.bodyNoFix"
      : `${kind}.body`;
  return {
    title: t(`notifications.cve.${kind}.title` as TranslationKey, params),
    body: t(`notifications.cve.${body}` as TranslationKey, params),
  };
}

// A type this client has no renderer for gets a neutral label: payload text is
// never shown, so an older client cannot display copy it did not localize.
const renderUnknown: Renderer = (n, t) => ({
  title: t("notifications.generic.title"),
  body: t(categoryKey(n.category)),
});

/** Where an item opens when its type and payload name no page of their own. */
export const NOTIFICATIONS_PATH = "/notifications";

export function renderNotification(
  n: InboxNotification,
  t: Translate,
  locale = "en",
): RenderedNotification {
  const view = (notificationRenderers[n.type] ?? renderUnknown)(n, t, locale);
  return {
    ...view,
    href: view.href ?? appPath(n.data.link) ?? NOTIFICATIONS_PATH,
  };
}

const knownCategories = ["security_alerts", "scan_results", "billing"] as const;

export function categoryKey(category: string): TranslationKey {
  return (
    (knownCategories as readonly string[]).includes(category)
      ? `notifications.category.${category}`
      : "notifications.category.other"
  ) as TranslationKey;
}

// Billing types (usage limits, payment failures, trial end). Severity is in
// the wording of each title, so nothing depends on colour.
const BILLING_PATH = "/settings/subscriptions";

const num = (value: unknown) => (typeof value === "number" ? value : 0);

function usageText(n: InboxNotification, t: Translate, locale: string) {
  const d = n.data;
  const metric = t(
    `notifications.type.usage.metric.${text(d.metric)}` as TranslationKey,
  );
  const format = new Intl.NumberFormat(locale);
  return {
    metric,
    params: {
      metric,
      used: format.format(num(d.used)),
      limit: format.format(num(d.limit)),
      percentage: format.format(Math.round(num(d.percentage))),
      plan: text(d.planName),
    },
  };
}

notificationRenderers["usage.limit_warning"] = (n, t, locale) => {
  const { metric, params } = usageText(n, t, locale);
  return {
    title: t("notifications.type.usage.limitWarning.title", { metric }),
    body: t("notifications.type.usage.limitWarning.body", params),
    href: BILLING_PATH,
  };
};

notificationRenderers["usage.limit_reached"] = (n, t, locale) => {
  const { metric, params } = usageText(n, t, locale);
  return {
    title: t("notifications.type.usage.limitReached.title", { metric }),
    body: t("notifications.type.usage.limitReached.body", params),
    href: BILLING_PATH,
  };
};

notificationRenderers["billing.payment_failed"] = (n, t, locale) => {
  const d = n.data;
  const retry = text(d.nextRetryDate);
  const params = {
    amount: new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
    }).format(num(d.amount)),
    plan: text(d.planName),
    attempt: num(d.attemptCount),
    date: retry
      ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
          new Date(retry),
        )
      : "",
  };
  return {
    title: t("notifications.type.billing.paymentFailed.title"),
    body: t(
      retry
        ? "notifications.type.billing.paymentFailed.body"
        : "notifications.type.billing.paymentFailed.bodyNoRetry",
      params,
    ),
    href: BILLING_PATH,
  };
};

notificationRenderers["billing.trial_ended"] = (n, t) => ({
  title: t("notifications.type.billing.trialEnded.title"),
  body: t("notifications.type.billing.trialEnded.body", {
    plan: text(n.data.planName),
  }),
  href: BILLING_PATH,
});
